import { ButtonBase } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '~components/button';
import Gender from '~components/gender';
import Typography from '~components/typography';
import StudentAvatarWithStatus from '~containers/student/detail/student-avatar-with-status';
import { BasicBirthday, BasicGlobal, BasicInstitution, BasicPhoneMobile } from '~svg-components';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '16px',
    width: '560px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    paddingBottom: '40px',
  },

  titleWrap: {
    marginBottom: '8px',
  },

  title: {
    cursor: 'pointer',

    '&:hover': {
      textDecoration: 'underline',
    },
  },

  email: {
    maxWidth: 240,
    overflow: 'hidden',
    marginRight: 8,
    marginBottom: 4,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },

  genderIcon: {
    marginLeft: '4px',
    width: '20px',
    height: '20px',
    transform: 'translateY(3px)',
  },

  createButton: {
    width: '320px',
    height: '48px',
  },

  searchAgain: {
    marginTop: '24px',
    cursor: 'pointer',
  },

  verified: {
    display: 'inline-block',
    padding: '0 8px',
    marginLeft: 4,
    borderRadius: 10,
    background: theme.colors.orangeBg,
  },

  info: {
    ...theme.typography.body1,
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    gridRowGap: '4px',
    gridColumnGap: '8px',

    '& > :nth-child(odd)': {
      color: theme.colors.greyCc,
    },

    '& > :nth-child(even)': {
      color: theme.colors.white,
    },
  },
  studentAvatar: {
    position: 'absolute',
    top: -28,
    left: '50%',
    height: '80px !important',
    width: '80px !important',
    transform: 'translateX(-50%)',
    cursor: 'pointer',
  },
  infoContainer: {
    position: 'relative',
    margin: '28px auto 32px',
    width: 320,
    padding: '64px 24px 32px',
    borderRadius: 8,
    boxShadow: '0 2px 16px 0 rgba(51, 53, 50, 0.08)',
  },
  divide: {
    width: 272,
    height: 1,
    backgroundColor: theme.colors.greyE8,
    margin: '20px 0 24px',
    border: 'none',
  },
  infoDetail: {
    paddingLeft: 16,
    marginBottom: 8,
    display: 'flex',
    alignItems: 'flex-start',

    '&:last-child': {
      marginBottom: 0,
    },
  },

  icon: {
    marginRight: 12,
    height: 24,
    fill: theme.colors.grey66,
  },
  unverifiedLabel: {
    padding: '0 8px',
    borderRadius: 20,
    backgroundColor: theme.colors.orangeBg,
    color: theme.colors.orangeDark,
  },
  rebookerLabel: {
    padding: '0 8px',
    borderRadius: 20,
    height: 20,
    backgroundColor: theme.colors.roseBg,
    color: theme.colors.roseDark,
  },
  emailContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
}));

function StepStudentFound(props) {
  const classes = useStyles();
  const { t } = useTranslation('booking');
  const { student, onClickBackToSearch, onClickCreateBooking } = props;

  const goToStudentPage = () => {
    window.open(
      queryString.stringifyUrl({
        url: '/student/student-details/personal-information',
        query: { studentId: student.id },
      }),
      '_blank'
    );
  };
  return (
    <div className={classes.root}>
      <div className={classes.infoContainer}>
        <StudentAvatarWithStatus
          className={classes.studentAvatar}
          src={student.profilePhotoFileUuid}
          bookings={student.bookings}
          StatusIconProps={{ size: 32 }}
          onClick={goToStudentPage}
        />

        <div className={classes.titleWrap}>
          <Typography
            variant="h2"
            align="center"
            themeColor="dark"
            className={classes.title}
            onClick={goToStudentPage}
          >
            {student.fullName}
            <Gender type={student.gender} className={classes.genderIcon} />
          </Typography>
        </div>

        <div className={classes.emailContainer}>
          <Typography
            variant="body1"
            themeColor="grey66"
            component="span"
            className={classes.email}
          >
            {student.email}
          </Typography>
          {!student?.account?.emailVerified && (
            <Typography variant="body2" component="span" className={classes.unverifiedLabel}>
              {t('unverified')}
            </Typography>
          )}
          {student?.account?.emailVerified && student.isRebooker && (
            <Typography variant="body2" component="span" className={classes.rebookerLabel}>
              {t('rebooker')}
            </Typography>
          )}
        </div>

        <hr className={classes.divide} />
        <Typography variant="body1" themeColor="dark" className={classes.infoDetail}>
          <BasicGlobal className={classes.icon} />
          {!!student?.generalInfo?.basic?.nationality
            ? student?.generalInfo?.basic?.nationality
            : '-'}
        </Typography>
        <Typography variant="body1" themeColor="dark" className={classes.infoDetail}>
          <BasicBirthday className={classes.icon} />
          {student?.generalInfo?.basic?.dateOfBirth
            ? format(parseISO(student?.generalInfo?.basic?.dateOfBirth), 'd MMM yyyy')
            : '-'}
        </Typography>
        <Typography variant="body1" themeColor="dark" className={classes.infoDetail}>
          <BasicPhoneMobile className={classes.icon} />
          {student?.generalInfo?.contact?.mobilePhoneAreaCode &&
          student?.generalInfo?.contact?.mobilePhoneNumber
            ? `(${student?.generalInfo?.contact?.mobilePhoneAreaCode}) ${student?.generalInfo?.contact?.mobilePhoneNumber}`
            : '-'}
        </Typography>
        <Typography variant="body1" themeColor="dark" className={classes.infoDetail}>
          <BasicInstitution className={classes.icon} />
          {!!student?.generalInfo?.education?.destinationUniversity
            ? student?.generalInfo?.education?.destinationUniversity
            : '-'}
        </Typography>
      </div>

      <Button
        key="confirm"
        className={classes.createButton}
        color="primary"
        onClick={() => onClickCreateBooking({ student })}
      >
        {t('booking.creating.student.create_booking')}
      </Button>

      <ButtonBase className={classes.searchAgain} onClick={onClickBackToSearch}>
        <Typography variant="button" themeColor="grey66">
          {t('booking.creating.student.back_to_search')}
        </Typography>
      </ButtonBase>
    </div>
  );
}

StepStudentFound.propTypes = {
  student: PropTypes.object.isRequired,
  onClickBackToSearch: PropTypes.func.isRequired,
  onClickCreateBooking: PropTypes.func.isRequired,
};

export default StepStudentFound;
